import React, { PureComponent } from 'react';
import { array, bool, func, number, oneOfType, shape, string } from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import MessageEditor from 'components/MessageEditor';
import DiversionSubform from 'components/Notifications/DiversionSubform';
import { Button } from '../../../Button';
import { SimpleText } from '../../../../util/IntlMessages';
import { DIVERSION_POLICIES, NOTICES } from '../../../../constants/Values';
import { bodyToSubformState } from '../../../../util/diversion';

const translations = {
  add: {
    buttonText: 'modal.noticeTemplate.addButtonText',
    headerText: 'modal.noticeTemplate.addHeader',
    headerTextDiversion: 'modal.noticeTemplate.addHeader.diversion',
  },
  edit: {
    buttonText: 'modal.noticeTemplate.editButtonText',
    headerText: 'modal.noticeTemplate.editHeader',
    headerTextDiversion: 'modal.noticeTemplate.editHeader.diversion',
  },
};

class NoticeTemplateDialog extends PureComponent {
  static propTypes = {
    addNoticeTemplate: func,
    editNoticeTemplate: func,
    hideNoticeTemplateModal: func,
    isOpen: bool,
    isLoading: bool,
    userRoles: array,
    template: shape({
      id: oneOfType([number, string]),
      body: string,
      header: string,
      type: string,
    }),
  };

  static defaultProps = {
    addNoticeTemplate: () => {},
    editNoticeTemplate: () => {},
    hideNoticeTemplateModal: () => {},
    isOpen: false,
    isLoading: false,
    userRoles: [],
    template: {
      id: '',
      body: '',
      header: '',
      type: '',
    },
  };

  state = {
    body: this.props.template.body || '',
    header: this.props.template.header || '',
    type: this.props.template.type || '',
    // Hydrate diversion from the template body when editing a DIVERSION template.
    // bodyToSubformState is tolerant — returns defaults for null/non-DIVERSION bodies.
    diversion:
      this.props.template.type === NOTICES.TYPES.DIVERSION
        ? bodyToSubformState(this.props.template.body)
        : {
            preamble: '',
            shelters: [],
            policy: DIVERSION_POLICIES.NEAREST,
            cancel: false,
          },
  };

  handleClose = () => {
    if (!this.props.isLoading) {
      this.props.hideNoticeTemplateModal();
    }
  };

  handleSubmitButtonClick = () => {
    const { type, header, diversion } = this.state;

    // For DIVERSION, construct the body via serializeBody (not raw state.body —
    // which is always empty for DIVERSION). notificationId is undefined for
    // templates (they're reusable, not bound to a specific in-flight diversion).
    const body =
      type === NOTICES.TYPES.DIVERSION
        ? DiversionSubform.serializeBody(diversion, undefined)
        : this.state.body;

    const payload = { type, header, body, diversion };

    if (this.props.template.id) {
      this.props.editNoticeTemplate(this.props.template.id, payload);
    } else {
      this.props.addNoticeTemplate(payload);
    }
  };

  /**
   * Lifted-state callback from <MessageEditor>.
   * Receives the full next { header, body, type, diversion } value object.
   */
  handleMessageEditorChange = nextContent => {
    this.setState({
      type: nextContent.type,
      header: nextContent.header,
      body: nextContent.body,
      diversion: nextContent.diversion,
    });
  };

  render() {
    const { template, isOpen, isLoading } = this.props;
    const { header, body, type, diversion } = this.state;

    const modalType = template.id ? 'edit' : 'add';

    const isDiversionTemplate = type === NOTICES.TYPES.DIVERSION;

    const isContentEmpty = isDiversionTemplate ? !DiversionSubform.validate(diversion).ok : !body;

    // Unchanged-from-template guard: disable the save button if the template
    // was opened for edit and nothing has changed.
    const isUnchanged = isDiversionTemplate
      ? template.type === NOTICES.TYPES.DIVERSION &&
        isEqual(diversion, bodyToSubformState(template.body)) &&
        header === template.header
      : body === template.body && header === template.header && type === template.type;

    const disabled = isContentEmpty || !header || !type || isUnchanged;

    return (
      <Dialog
        open={isOpen}
        onClose={this.handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth
        className="notice-template-dialog"
      >
        <DialogTitle id="form-dialog-title" className="dialog-title">
          <SimpleText
            id={
              isDiversionTemplate
                ? translations[modalType].headerTextDiversion
                : translations[modalType].headerText
            }
          />
          <IconButton color="inherit" onClick={this.handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent // pt keeps the !important the old p-3 utility had — MUI's title-adjacent
          // rule (.MuiDialogTitle-root + &) zeroes padding-top past plain sx.
          sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', p: 2, pt: '16px !important' }}>
          <MessageEditor
            value={{ header, body, type, diversion }}
            onChange={this.handleMessageEditorChange}
            availableTypes={[
              NOTICES.TYPES.GENERAL,
              NOTICES.TYPES.DELAY,
              NOTICES.TYPES.SECURITY,
              NOTICES.TYPES.DIVERSION,
            ]}
            userRoles={this.props.userRoles}
            validationErrors={{}}
            disabled={this.props.isLoading}
          />
        </DialogContent>

        {isLoading && (
          <div className="loader">
            <CircularProgress size={50} />
          </div>
        )}

        <DialogActions>
          <Button
            size="large"
            labelId={translations[modalType].buttonText}
            variant="contained"
            color="primary"
            onClick={this.handleSubmitButtonClick}
            disabled={disabled}
          />
        </DialogActions>
      </Dialog>
    );
  }
}

// Named export for tests — avoids need for Redux store in spec setup.
export { NoticeTemplateDialog };

const mapStateToProps = state => ({
  userRoles: (state.profile.user && state.profile.user.roles) || [],
});

export default connect(mapStateToProps)(injectIntl(NoticeTemplateDialog));
